const BASE_URL = process.env.REACT_APP_BASE_URL;

// Start Video API - returns task_id and video_project_id for WebSocket tracking
const startVideoGeneration = async (publicationId, token, professionalType, selectedSections, selectedMusic, videoId) => {
  try {
    // Standard JSON request
    const url = `${BASE_URL}/video_start?videoId=${videoId}`;
    console.log("[startVideoGeneration] URL:", url);

    const templateIds = selectedSections.flatMap(section =>
      Array.isArray(section.template_ids) ? section.template_ids : [section.template_ids]
    ).filter(Boolean);

    console.log("[startVideoGeneration] Template IDs to send:", templateIds);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(templateIds),
    });

    console.log("[startVideoGeneration] Response status:", response.status);

    if (!response.ok) {
      // Check for 500 error specifically
      if (response.status === 500) {
        return { error: "Oops! Something went wrong on our end." };
      }
      const errorResponse = await response.json();
      console.error("Error starting video generation:", errorResponse);
      return { error: "Failed to start video generation. Please try again." };
    }
    return await response.json();
  } catch (error) {
    console.error("Error in startVideoGeneration:", error);
    console.error("Error details:", error.message);
    return { error: "An unexpected error occurred while starting video generation." };
  }
};

// Regenerate Video: calls /regenerate_failed_video with an existing videoId (for failed projects)
const regenerateVideoGeneration = async (token, videoId) => {
  try {
    const url = `${BASE_URL}/regenerate_failed_video?project_id=${videoId}`;
    console.log("[regenerateVideoGeneration] URL:", url);

    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    });

    if (!response.ok) {
      try {
        const errorResponse = await response.json();
        console.error("[regenerateVideoGeneration] Backend error:", errorResponse);
      } catch (_) { }
      return { error: "Something went wrong. Please try again." };
    }
    return await response.json();
  } catch (error) {
    console.error("[regenerateVideoGeneration] Unexpected error:", error);
    return { error: "Something went wrong. Please try again." };
  }
};

export const handleStartGenerating = async ({
  publicationId,
  token,
  contentLevel,
  selectedSections,
  selectedMusic,
  setActiveTasks,
  setTaskStatus,
  setAlert,
  setupWebSocket,
  setOpenAbstractVideoDialog,
  setVideoProjectId,
  existingVideoProjectId,
  setRefreshTrigger,
}) => {
  // Close dialog immediately when user clicks generate
  setOpenAbstractVideoDialog(false);

  // Mark video as actively generating BEFORE the API call so the card enters
  // "generating" state immediately (matches PPT handler behaviour).
  // All error/failure branches below reset this to false if the call fails.
  const uniqueId = existingVideoProjectId || "pending";
  setActiveTasks((prev) => {
    const nextArr = [...(prev.video || [])];
    if (!nextArr.includes(uniqueId)) nextArr.push(uniqueId);
    return { ...prev, video: nextArr };
  });
  localStorage.setItem("activeTasks", "true");
  setTaskStatus(prev => ({
    ...prev,
    video: {
      ...(prev.video || {}),
      [uniqueId]: { progress: 0, status: "queued", publicationId: Number(publicationId), video_project_id: existingVideoProjectId || null }
    }
  }));

  setAlert({ severity: "info", message: "Starting video generation..." });

  try {
    const professionalType = contentLevel || "Professionals";

    console.log("[handleStartGenerating] Starting video generation with professionalType:", professionalType, "selectedMusic:", selectedMusic);
    console.log("[handleStartGenerating] Selected sections:", selectedSections);

    const result = await startVideoGeneration(
      publicationId,
      token,
      professionalType,
      selectedSections,
      selectedMusic,
      existingVideoProjectId
    );

    console.log("[handleStartGenerating] API Result:", result);

    if (result.error) {
      setAlert({
        severity: "warning",
        message: result.error,
      });
      if (!existingVideoProjectId) {
        setActiveTasks(prev => ({ ...prev, video: (prev.video || []).filter(id => id !== "pending") }));
      } else {
        setActiveTasks(prev => ({ ...prev, video: (prev.video || []).filter(id => id !== existingVideoProjectId) }));
        setTaskStatus(prev => ({
          ...prev,
          video: {
            ...(prev.video || {}),
            [existingVideoProjectId]: { ...(prev.video?.[existingVideoProjectId] || {}), progress: 0, status: "error" }
          }
        }));
      }
    } else if (result.task_id) {
      // Store the video_project_id for later use when viewing content
      if (result.video_project_id && setVideoProjectId) {
        console.log("[handleStartGenerating] Storing video_project_id:", result.video_project_id);
        setVideoProjectId(result.video_project_id);
      }

      if (!existingVideoProjectId) {
        setActiveTasks(prev => ({ ...prev, video: (prev.video || []).filter(id => id !== "pending") }));
      }

      // Setup WebSocket for progress tracking
      console.log("[handleStartGenerating] Setting up WebSocket with task_id:", result.task_id);
      setupWebSocket(result.task_id, "video", publicationId, result.video_project_id, (message, severity) => {
        setAlert({ severity, message });
      });
      setAlert({
        severity: "success",
        message: " Video Generation Started",
      });
      if (setRefreshTrigger) {
        setRefreshTrigger(prev => prev + 1);
      }
    } else {
      // No task_id returned - API might have different response format
      console.log("[handleStartGenerating] No task_id in response. Full response:", result);
      setAlert({
        severity: "warning",
        message: "Unexpected response from server. Please check if generation started.",
      });
      if (!existingVideoProjectId) {
        setActiveTasks(prev => ({ ...prev, video: (prev.video || []).filter(id => id !== "pending") }));
      } else {
        setActiveTasks(prev => ({ ...prev, video: (prev.video || []).filter(id => id !== existingVideoProjectId) }));
        setTaskStatus(prev => ({
          ...prev,
          video: {
            ...(prev.video || {}),
            [existingVideoProjectId]: { ...(prev.video?.[existingVideoProjectId] || {}), progress: 0, status: "error" }
          }
        }));
      }
    }
  } catch (error) {
    console.error("[handleStartGenerating] Error:", error);
    setAlert({
      severity: "warning",
      message: "Failed to start video generation. Please try again.",
    });
    if (!existingVideoProjectId) {
      setActiveTasks(prev => ({ ...prev, video: (prev.video || []).filter(id => id !== "pending") }));
    } else {
      setActiveTasks(prev => ({ ...prev, video: (prev.video || []).filter(id => id !== existingVideoProjectId) }));
      setTaskStatus(prev => ({
        ...prev,
        video: {
          ...(prev.video || {}),
          [existingVideoProjectId]: { ...(prev.video?.[existingVideoProjectId] || {}), progress: 0, status: "error" }
        }
      }));
    }
  }
};

export const handleRegenerateVideo = async ({
  videoId,
  publicationId,
  token,
  setActiveTasks,
  setTaskStatus,
  setAlert,
  setupWebSocket,
  setRefreshTrigger,
}) => {
  const uniqueId = videoId || "pending";
  try {
    setActiveTasks((prev) => {
      const nextArr = [...(prev.video || [])];
      if (!nextArr.includes(uniqueId)) nextArr.push(uniqueId);
      return { ...prev, video: nextArr };
    });
    localStorage.setItem("activeTasks", "true");
    setTaskStatus(prev => ({
      ...prev,
      video: {
        ...(prev.video || {}),
        [uniqueId]: { progress: 0, status: "queued", publicationId: Number(publicationId), video_project_id: videoId || null }
      }
    }));

    console.log("[handleRegenerateVideo] Regenerating videoId:", videoId);

    const result = await regenerateVideoGeneration(token, videoId);
    console.log("[handleRegenerateVideo] API Result:", result);

    if (result.error) {
      setAlert({ severity: "warning", message: result.error });
      setActiveTasks(prev => ({ ...prev, video: (prev.video || []).filter(id => id !== uniqueId) }));
      setTaskStatus(prev => { const n = { ...prev }; if (n.video) { n.video = { ...n.video }; delete n.video[uniqueId]; } return n; });
    } else if (result.task_id) {
      setActiveTasks(prev => ({ ...prev, video: (prev.video || []).filter(id => id !== uniqueId) }));
      setTaskStatus(prev => { const n = { ...prev }; if (n.video) { n.video = { ...n.video }; delete n.video[uniqueId]; } return n; });
      setupWebSocket(result.task_id, "video", publicationId, videoId, (message, severity) => {
        setAlert({ severity, message });
      });
      setAlert({ severity: "success", message: "Video Regeneration Started" });
      if (setRefreshTrigger) {
        setRefreshTrigger(prev => prev + 1);
      }
    } else {
      console.log("[handleRegenerateVideo] Unexpected response:", result);
      setAlert({ severity: "warning", message: "Unexpected response from server." });
      setActiveTasks(prev => ({ ...prev, video: (prev.video || []).filter(id => id !== uniqueId) }));
      setTaskStatus(prev => { const n = { ...prev }; if (n.video) { n.video = { ...n.video }; delete n.video[uniqueId]; } return n; });
    }
  } catch (error) {
    console.error("[handleRegenerateVideo] Error:", error);
    setAlert({ severity: "warning", message: "Failed to regenerate Video. Please try again." });
    setActiveTasks(prev => ({ ...prev, video: (prev.video || []).filter(id => id !== uniqueId) }));
    setTaskStatus(prev => { const n = { ...prev }; if (n.video) { n.video = { ...n.video }; delete n.video[uniqueId]; } return n; });
  }
};

export const handleFileChange = (e, setLogoFile) => {
  if (e.target.files && e.target.files[0]) {
    setLogoFile(e.target.files[0]);
  }
};

export const handleOpenAbstractVideoDialog = (setOpenAbstractVideoDialog) => {
  setOpenAbstractVideoDialog(true);
};

export const handleCloseAbstractVideoDialog = (setOpenAbstractVideoDialog) => {
  setOpenAbstractVideoDialog(false);
};
